/**
 * Creates an action wrapper that runs effects at different lifecycle points
 * @param {Object} options - Action configuration options
 * @param {string} options.name - The action name
 * @param {string} [options.description=''] - Human-readable description of the action
 * @param {Function} [options.enabled] - Function that returns boolean for enabled state (defaults to () => false)
 * @returns {Function} Wrapped action function
 */
export function createAction(options = {}) {
  const {
    name,
    description = '',
    enabled = () => false
  } = options;

  if (!name) {
    throw new Error('Action must have a name');
  }

  // Create the wrapped action function
  const wrappedAction = async (...args) => {
    const startTime = performance.now();
    const actionContext = {
      action: wrappedAction,
      args: [...args], // Mutable by effects for data passing
      name,
      metadata: wrappedAction.metadata || {},
      timestamp: Date.now()
      // Effects can add arbitrary properties for data passing between lifecycle phases
    };

    // Helper runners that run effects in parallel within each phase
    const runBefore = async () => {
      // Call global listeners first
      await callGlobalListeners(actionContext, 'before');
      
      const handlers = beforeEffects.get(wrappedAction) || [];
      if (handlers.length === 0) return;
      
      await Promise.all(handlers.map(async (handler) => {
        try { 
          return await handler(actionContext);
        } catch (error) { 
          throw error; // Re-throw to stop execution
        }
      }));
    };

    const runOn = async () => {
      // Call global listeners first
      await callGlobalListeners(actionContext, 'on');
      
      const handlers = onEffects.get(wrappedAction) || [];
      if (handlers.length === 0) return;
      
      await Promise.all(handlers.map(async (handler) => {
        try { 
          await handler(actionContext);
        } catch (error) { 
          console.warn('Effect error (on):', error);
          throw error; // Re-throw to stop execution
        }
      }));
    };

    const runAfter = async () => {
      // Call global listeners first
      await callGlobalListeners(actionContext, 'after');
      
      const handlers = afterEffects.get(wrappedAction) || [];
      if (handlers.length === 0) return;
      
      await Promise.all(handlers.map(async (handler) => {
        try { 
          await handler(actionContext);
        } catch (error) { 
          console.warn('Effect error (after):', error);
          throw error; // Re-throw to stop execution
        }
      }));
    };

    const runError = async (error) => {
      // Call global listeners first
      await callGlobalListeners(actionContext, 'error', error);
      
      const handlers = errorEffects.get(wrappedAction) || [];
      if (handlers.length === 0) return;
      
      await Promise.all(handlers.map(async (handler) => {
        try { 
          await handler(actionContext, error);
        } catch (err) { 
          console.warn('Effect error (error):', err);
          // Don't re-throw in error handlers to avoid infinite loops
        }
      }));
    };

    const runCleanup = async () => {
      // Call global listeners first
      await callGlobalListeners(actionContext, 'cleanup');
      
      const handlers = cleanupEffects.get(wrappedAction) || [];
      if (handlers.length === 0) return;
      
      await Promise.all(handlers.map(async (handler) => {
        try { 
          await handler(actionContext);
        } catch (err) { 
          console.warn('Effect error (cleanup):', err);
          // Don't re-throw in cleanup handlers to ensure cleanup always completes
        }
      }));
    };

    const executeAction = async () => {
      try {
        // Phase 1: Run before effects (data preparation, validation, ID generation)
        await runBefore();
        // Phase 2: Run on effects (core processing)
        await runOn();
        // Phase 3: Run after effects (side effects)
        await runAfter();
        
        return actionContext; // Return the context with any data added by effects
      } catch (error) {
        // Phase 4: Run error effects (rollback, error logging)
        await runError(error);
        
        throw error; // Re-throw to parent action
      } finally {
        // Phase 5: Run cleanup effects (always runs, resource cleanup)
        await runCleanup();
      }
    };

    return executeAction();
  };

  // Add name property to the function (use Object.defineProperty for strict mode compatibility)
  Object.defineProperty(wrappedAction, 'name', {
    value: name,
    configurable: true
  });
  
  // Add metadata storage
  wrappedAction.metadata = {};
  
  // Add set method for injecting metadata like category
  wrappedAction.set = function(key, value) {
    this.metadata[key] = value;
  };

  // Register the action in the global registry
  registerAction(name, wrappedAction, enabled, description);

  return wrappedAction;
}

// Action registry
const actionRegistry = new Map();

/**
 * Registers an action in the global registry
 * @param {string} name - The action name
 * @param {Function} actionFn - The action function
 * @param {Function|null} enabledFn - Function to determine if action is enabled
 * @param {string} description - Action description
 */
function registerAction(name, actionFn, enabledFn = null, description = '') {
  // Warn about overwrites in development
  if (process.env.NODE_ENV === 'development') {
    const existing = actionRegistry.get(name);
    if (existing) {
      console.warn(`Action "${name}" already registered - overwriting existing action`);
    }
  }

  actionRegistry.set(name, {
    name,
    fn: actionFn,
    enabledFn,
    description,
    registeredAt: Date.now()
  });
}

// Effect registries - using Maps with action functions as keys
const beforeEffects = new Map();
const onEffects = new Map();
const afterEffects = new Map();
const errorEffects = new Map();
const cleanupEffects = new Map();

/**
 * Register effect handlers to run before specific actions
 * @param {Function|Function[]} actions - Action function(s) to watch
 * @param {Function} handler - Handler function to run before action
 */
export function before(actions, handler) {
  const actionList = Array.isArray(actions) ? actions : [actions];
  
  actionList.forEach(action => {
    if (!beforeEffects.has(action)) {
      beforeEffects.set(action, []);
    }
    
    beforeEffects.get(action).push(handler);
  });
}

/**
 * Register effect handlers to run during specific actions (between before and after)
 * @param {Function|Function[]} actions - Action function(s) to watch
 * @param {Function} handler - Handler function to run during action, receives (actionContext)
 */
export function on(actions, handler) {
  const actionList = Array.isArray(actions) ? actions : [actions];
  
  actionList.forEach(action => {
    if (!onEffects.has(action)) {
      onEffects.set(action, []);
    }
    
    onEffects.get(action).push(handler);
  });
}

/**
 * Register effect handlers to run after specific actions
 * @param {Function|Function[]} actions - Action function(s) to watch
 * @param {Function} handler - Handler function to run after action, receives (actionContext)
 */
export function after(actions, handler) {
  const actionList = Array.isArray(actions) ? actions : [actions];
  
  actionList.forEach(action => {
    if (!afterEffects.has(action)) {
      afterEffects.set(action, []);
    }
    
    afterEffects.get(action).push(handler);
  });
}

/**
 * Register effect handlers to run when specific actions throw errors
 * @param {Function|Function[]} actions - Action function(s) to watch
 * @param {Function} handler - Handler function to run when action throws error
 */
export function error(actions, handler) {
  const actionList = Array.isArray(actions) ? actions : [actions];
  
  actionList.forEach(action => {
    if (!errorEffects.has(action)) {
      errorEffects.set(action, []);
    }
    
    errorEffects.get(action).push(handler);
  });
}

/**
 * Register effect handlers to run after specific actions complete (success or error)
 * @param {Function|Function[]} actions - Action function(s) to watch
 * @param {Function} handler - Handler function to run for cleanup
 */
export function cleanup(actions, handler) {
  const actionList = Array.isArray(actions) ? actions : [actions];
  
  actionList.forEach(action => {
    if (!cleanupEffects.has(action)) {
      cleanupEffects.set(action, []);
    }
    
    cleanupEffects.get(action).push(handler);
  });
}

/**
 * Clears all effects from the registries (for testing)
 */
export function clearEffects() {
  beforeEffects.clear();
  onEffects.clear();
  afterEffects.clear();
  errorEffects.clear();
  cleanupEffects.clear();
  clearGlobalListeners();
}

/**
 * Clears all actions from the registry (for testing)
 */
export function clearRegistry() {
  actionRegistry.clear();
}

/**
 * Query actions based on their enabled state
 * @param {Object} context - Context object passed to enabled functions
 * @returns {Array} Array of enabled actions
 */
export function queryActions(context = {}) {
  const enabledActions = [];
  
  for (const [name, actionEntry] of actionRegistry.entries()) {
    let isEnabled = true;
    
    // Call the enabled function if it exists
    if (actionEntry.enabledFn && typeof actionEntry.enabledFn === 'function') {
      try {
        isEnabled = actionEntry.enabledFn(context);
      } catch (error) {
        console.warn(`Error evaluating enabled state for action "${name}":`, error);
        isEnabled = false;
      }
    }
    
    if (isEnabled) {
      enabledActions.push({
        name: actionEntry.name,
        fn: actionEntry.fn,
        description: actionEntry.description,
        metadata: actionEntry.fn.metadata || {},
        registeredAt: actionEntry.registeredAt
      });
    }
  }
  
  return enabledActions;
}

/**
 * Gets a specific action by name
 * @param {string} name - Action name
 * @returns {Object|null} Action entry or null if not found
 */
export function getAction(name) {
  const actionEntry = actionRegistry.get(name);
  if (!actionEntry) return null;
  
  return {
    name: actionEntry.name,
    fn: actionEntry.fn,
    description: actionEntry.description,
    metadata: actionEntry.fn.metadata || {},
    registeredAt: actionEntry.registeredAt
  };
}

/**
 * Checks if an action exists
 * @param {string} name - Action name
 * @returns {boolean} True if action exists
 */
export function hasAction(name) {
  return actionRegistry.has(name);
}

/**
 * Gets all registered actions (regardless of enabled state)
 * @returns {Array} Array of all action entries
 */
export function getAllActions() {
  return Array.from(actionRegistry.values()).map(actionEntry => ({
    name: actionEntry.name,
    fn: actionEntry.fn,
    description: actionEntry.description,
    metadata: actionEntry.fn.metadata || {},
    registeredAt: actionEntry.registeredAt
  }));
}

/**
 * Gets registry statistics
 * @returns {Object} Registry stats
 */
export function getActionStats() {
  return {
    total: actionRegistry.size
  };
}

// Global action listeners - called on every action at every lifecycle phase
const globalListeners = new Set();

/**
 * Adds a global listener that will be called for every action at every lifecycle phase
 * @param {Function} listener - Function that receives (actionContext, phase, ...args)
 * @returns {Function} Unsubscribe function
 */
export function addGlobalListener(listener) {
  if (typeof listener !== 'function') {
    throw new Error('Global listener must be a function');
  }
  
  globalListeners.add(listener);
  
  // Return unsubscribe function
  return () => {
    globalListeners.delete(listener);
  };
}

/**
 * Removes a global listener
 * @param {Function} listener - The listener function to remove
 */
export function removeGlobalListener(listener) {
  globalListeners.delete(listener);
}

/**
 * Calls all global listeners with the provided arguments
 * @param {Object} actionContext - The action context
 * @param {string} phase - The lifecycle phase ('before', 'on', 'after', 'error', 'cleanup')
 * @param {...any} args - Additional arguments to pass to listeners
 */
async function callGlobalListeners(actionContext, phase, ...args) {
  if (globalListeners.size === 0) return;
  
  // Add phase directly to actionContext so store reducers that set context.data
  // (or other properties) are mutating the same object that will be returned to
  // the caller. Creating a copy here would silently discard those mutations.
  actionContext.phase = phase;
  
  // Call all listeners in parallel
  await Promise.all(
    Array.from(globalListeners).map(async (listener) => {
      try {
        await listener(actionContext, phase, ...args);
      } catch (error) {
        console.warn('Global listener error:', error);
        // Don't re-throw to prevent one listener from breaking others
      }
    })
  );
}

/**
 * Clears all global listeners (for testing)
 */
export function clearGlobalListeners() {
  globalListeners.clear();
}